import { Box, Grid2, Stack, Switch, Typography } from '@mui/material';
import { Controller, useFormContext } from 'react-hook-form';

import { ReadMore } from 'src/components/ReadMore';
import { colors } from 'src/global/styles/colors';
import { ProjectInputs } from 'src/global/types/project.type';
import { useDeployment } from 'src/provider/DeploymentProvider';
import { isAllowedGreadterThanZero } from 'src/utils/numberFormat';

import { AdminNumberFormat } from '../AdminNumberFormat';

export default function AutoPriceAdjustment({ isDisabled = false }: { isDisabled?: boolean }) {
  const {
    control,
    watch,
    formState: { errors },
  } = useFormContext<ProjectInputs>();
  const watchAutoPriceAdjustment = watch('tier.autoAdjustment');
  const { hasDeployed } = useDeployment();

  return (
    <Box marginTop={3}>
      <Stack paddingBottom={2} direction={'row'} justifyContent={'space-between'} alignItems={'center'}>
        <Typography fontSize={'16px'} color='white' lineHeight={'20px'} fontWeight={500} variant='h6'>
          Tiered Sales with Automatic Price Adjustments per Tier
        </Typography>
        <Controller
          name='tier.autoAdjustment'
          control={control}
          render={({ field: { onChange, value } }) => (
            <Switch readOnly={isDisabled} disabled={hasDeployed} checked={value} onChange={onChange} />
          )}
        />
      </Stack>

      <ReadMore
        typoProps={{
          fontSize: '14px',
          color: 'white',
          lineHeight: '20px',
          paddingBottom: 3,
        }}
        id='auto-price'
        text='This feature lets users set a fixed supply for each tier with automatic price increases. Once the supply is sold out, the price moves to the next tier. The Price Cap sets the maximum price. If a previous tiered sales structure exists, it will run first before this setup.'
      />
      {watchAutoPriceAdjustment ? (
        <Grid2 container spacing={3}>
          <Grid2 size={6}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Supply in Tier
            </Typography>
            <Controller
              name={`tier.supplyIn`}
              control={control}
              render={({ field }) => (
                <AdminNumberFormat
                  isAllowed={isAllowedGreadterThanZero}
                  decimalScale={0}
                  isError={!!errors?.tier?.supplyIn}
                  errorMsg={errors?.tier?.supplyIn?.message}
                  placeholder={'0'}
                  {...field}
                  onValueChange={e => {
                    field.onChange(Number(e.floatValue));
                  }}
                  suffix=''
                  readOnly={isDisabled || hasDeployed}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Price Increase in USD per Tier
            </Typography>
            <Controller
              name={`tier.pricePer`}
              control={control}
              render={({ field }) => (
                <AdminNumberFormat
                  isError={!!errors?.tier?.pricePer}
                  isAllowed={isAllowedGreadterThanZero}
                  errorMsg={errors?.tier?.pricePer?.message}
                  placeholder={'$0.0'}
                  {...field}
                  onValueChange={e => {
                    field.onChange(Number(e.floatValue));
                  }}
                  prefix='$'
                  suffix=''
                  readOnly={isDisabled || hasDeployed}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              NFT Supply Cap
            </Typography>
            <Controller
              name={`tier.nodeSupplyCap`}
              control={control}
              render={({ field }) => (
                <AdminNumberFormat
                  isAllowed={isAllowedGreadterThanZero}
                  decimalScale={0}
                  isError={!!errors?.tier?.nodeSupplyCap}
                  errorMsg={errors?.tier?.nodeSupplyCap?.message}
                  placeholder={'0'}
                  {...field}
                  onValueChange={e => {
                    field.onChange(Number(e.floatValue));
                  }}
                  prefix=''
                  suffix=''
                  readOnly={isDisabled || hasDeployed}
                />
              )}
            />
          </Grid2>
          <Grid2 size={6}>
            <Typography paddingBottom={1} color={colors.neutral200} fontSize={'14px'} variant='h6'>
              Price Cap
            </Typography>
            <Controller
              name={`tier.priceCap`}
              control={control}
              render={({ field }) => (
                <AdminNumberFormat
                  isAllowed={isAllowedGreadterThanZero}
                  isError={!!errors?.tier?.priceCap}
                  errorMsg={errors?.tier?.priceCap?.message}
                  placeholder={'$0.0'}
                  {...field}
                  onValueChange={e => {
                    field.onChange(Number(e.floatValue));
                  }}
                  prefix='$'
                  suffix=''
                  readOnly={isDisabled || hasDeployed}
                />
              )}
            />
          </Grid2>
        </Grid2>
      ) : null}
    </Box>
  );
}
